import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import { ChevronLeft, ChevronRight, Edit2, Plus } from "lucide-react";
import EditDayModalLegal from "@/components/time/EditDayModalLegal";
import moment from "moment";
import "moment/locale/ca";
moment.locale("ca");

// Returns total worked minutes from an array of records for a single worker on a single day
function calcWorkedMinutes(records) {
  const sorted = [...records].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  let total = 0;
  let entryTime = null;
  let pauseStart = null;

  for (const r of sorted) {
    if (r.type === "entry") entryTime = new Date(r.timestamp);
    else if (r.type === "pause_start" && entryTime) pauseStart = new Date(r.timestamp);
    else if (r.type === "pause_end" && pauseStart) pauseStart = null;
    else if (r.type === "exit" && entryTime) {
      const exitTime = new Date(r.timestamp);
      total += (exitTime - entryTime) / 60000;
      entryTime = null;
    }
  }
  // If still clocked in (no exit), count up to now
  if (entryTime) {
    total += (new Date() - entryTime) / 60000;
  }
  return Math.max(0, Math.round(total));
}

function minutesToHHMM(mins) {
  if (!mins) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m.toString().padStart(2, "0")}m`;
}

function getEntryExit(records) {
  const sorted = [...records].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const entry = sorted.find(r => r.type === "entry");
  const exits = sorted.filter(r => r.type === "exit");
  const exit = exits[exits.length - 1];
  return {
    entry: entry ? moment(entry.timestamp).format("HH:mm") : null,
    exit: exit ? moment(exit.timestamp).format("HH:mm") : null,
  };
}

function DayCell({ records, date, member, isAdmin, onEdit }) {
  const mins = calcWorkedMinutes(records);
  const { entry, exit } = getEntryExit(records);
  const hasRecords = records.length > 0;
  const isToday = date === moment().format("YYYY-MM-DD");
  const isFuture = date > moment().format("YYYY-MM-DD");

  if (isFuture) {
    return <td className="px-3 py-3 text-center text-gray-200 text-xs">—</td>;
  }

  return (
    <td className={`px-2 py-2 text-center align-middle ${isToday ? "bg-[#7B2D3F]/5" : ""}`}>
      {hasRecords ? (
        <div className="flex flex-col items-center gap-0.5">
          <span className="font-semibold text-gray-800 text-sm">{minutesToHHMM(mins)}</span>
          <span className="text-[10px] text-gray-400">{entry} → {exit || "actiu"}</span>
          {isAdmin && (
            <button
              onClick={() => onEdit(member, date, records)}
              className="text-[10px] text-[#7B2D3F] hover:underline mt-0.5 flex items-center gap-0.5"
            >
              <Edit2 className="w-2.5 h-2.5" /> editar
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center">
          <span className="text-gray-200 text-xs">—</span>
          {isAdmin && (
            <button
              onClick={() => onEdit(member, date, [])}
              className="text-[10px] text-[#7B2D3F] hover:underline mt-0.5 flex items-center gap-0.5"
            >
              <Plus className="w-2.5 h-2.5" /> afegir
            </button>
          )}
        </div>
      )}
    </td>
  );
}


export default function WeeklyScheduleView({ venueId, isAdmin }) {
  const { user } = useVenue();
  const [weekStart, setWeekStart] = useState(moment().startOf("isoWeek"));
  const [members, setMembers] = useState([]);
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(null); // { member, date, records }

  const days = Array.from({ length: 7 }, (_, i) => weekStart.clone().add(i, "days"));
  const dayStrings = days.map(d => d.format("YYYY-MM-DD"));

  useEffect(() => {
    if (!venueId) return;
    loadData();
  }, [venueId, weekStart.format()]);

  const loadData = async () => {
    setLoading(true);
    const [mems, recs] = await Promise.all([
      hosys.entities.VenueMember.filter({ venue_id: venueId, active: true }),
      hosys.entities.TimeRecord.filter({ venue_id: venueId }),
    ]);
    // Filter records for this week
    const weekRecs = recs.filter(r => dayStrings.includes(r.date));
    setMembers(mems);
    setAllRecords(weekRecs);
    setLoading(false);
  };

  const getRecordsForMemberDay = (memberEmail, date) =>
    allRecords.filter(r => r.user_email === memberEmail && r.date === date);

  const getTotalForMember = (memberEmail) => {
    let total = 0;
    dayStrings.forEach(d => {
      const recs = getRecordsForMemberDay(memberEmail, d);
      total += calcWorkedMinutes(recs);
    });
    return total;
  };

  const prevWeek = () => setWeekStart(w => w.clone().subtract(1, "week"));
  const nextWeek = () => setWeekStart(w => w.clone().add(1, "week"));
  const isCurrentWeek = weekStart.isSame(moment().startOf("isoWeek"), "day");

  if (loading) return <div className="flex items-center justify-center py-12 text-gray-400 text-sm">Carregant horaris...</div>;

  return (
    <div>
      {/* Week navigator */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button onClick={prevWeek} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-sm font-semibold text-gray-700 capitalize">
            {weekStart.format("D MMM")} – {weekStart.clone().endOf("isoWeek").format("D MMM YYYY")}
          </span>
          <button onClick={nextWeek} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        {!isCurrentWeek && (
          <button onClick={() => setWeekStart(moment().startOf("isoWeek"))} className="text-xs text-[#7B2D3F] hover:underline">
            Setmana actual
          </button>
        )}
      </div>

      {members.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">
          No hi ha treballadors configurats. Afegeix personal des de la pestanya "Personal".
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100 shadow-sm bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-4 py-3 font-semibold text-gray-600 w-40">Treballador</th>
                {days.map(d => {
                  const isToday = d.format("YYYY-MM-DD") === moment().format("YYYY-MM-DD");
                  return (
                    <th key={d.format()} className={`px-2 py-3 text-center font-semibold text-xs ${isToday ? "text-[#7B2D3F]" : "text-gray-500"}`}>
                      <div className="capitalize">{d.format("ddd")}</div>
                      <div className={`text-base font-bold mt-0.5 ${isToday ? "text-[#7B2D3F]" : "text-gray-800"}`}>{d.format("D")}</div>
                    </th>
                  );
                })}
                <th className="px-3 py-3 text-center font-semibold text-xs text-gray-500">Total</th>
              </tr>
            </thead>
            <tbody>
              {members.map(member => (
                <tr key={member.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800 text-sm truncate max-w-[140px]">{member.user_name || member.user_email}</div>
                    {member.position && <div className="text-[10px] text-gray-400">{member.position}</div>}
                  </td>
                  {dayStrings.map(date => (
                    <DayCell
                      key={date}
                      date={date}
                      records={getRecordsForMemberDay(member.user_email, date)}
                      member={member}
                      isAdmin={isAdmin}
                      onEdit={(m, d, r) => setEditModal({ member: m, date: d, records: r })}
                    />
                  ))}
                  <td className="px-3 py-3 text-center">
                    <span className="font-bold text-gray-800 text-sm">{minutesToHHMM(getTotalForMember(member.user_email))}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editModal && (
        <EditDayModalLegal
          member={editModal.member}
          date={editModal.date}
          records={editModal.records}
          venueId={venueId}
          currentUser={user}
          onClose={() => setEditModal(null)}
          onSaved={() => { setEditModal(null); loadData(); }}
        />
      )}
    </div>
  );
}